import { IMap, IUserBase } from '@shared/interfaces';
import { IGroupOfUsers, ITeamType } from '../interfaces';
import { allMemberTypes, TEAM_MEMBER_TYPES, TTeamMemberType } from './models/RoleAssignmentModel';
import { IRoleAssignmentSelection, ITeamSelection, ITeamTypeSelection } from './models/roleAssignmentSelectionModel';

export function filterDuplicate(arr: any[]) {
  const result: any[] = [];
  const map = new Map();

  arr.forEach((obj: {
    userId: string;
    userName: string;
    assignedFrom: string;
  }) => {
    if (map.has(obj.userId)) {
      const existingObj = map.get(obj.userId);
      if (!existingObj.assignedFrom.includes(obj.assignedFrom)) {
        existingObj.assignedFrom += (existingObj.assignedFrom ? ', ' : '') + obj.assignedFrom;
      }
    } else {
      map.set(obj.userId, { userId: obj.userId, userName: obj.userName, assignedFrom: obj.assignedFrom });
    }
  });

  map.forEach(value => {
    result.push(value);
  });

  return result;
}

export const mapAssignedFrom =
  (assignedFrom: string) =>
  <T>(user: T) => ({ ...user, assignedFrom });

const teamIncludes = (team: ITeamSelection, memberType: TTeamMemberType) => team.includedTeamMemberType.indexOf(memberType) >= 0;
const teamTypeIncludes = (teamType: ITeamTypeSelection, memberType: TTeamMemberType) =>
  teamType.includedTeamMemberType.indexOf(memberType) >= 0;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const collectAllSelectedUsers = <T = IUserBase & { assignedFrom: string }>(groupOfUsers: IRoleAssignmentSelection) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let allUsers: any[] = [];

  groupOfUsers.teamType.forEach((teamType) => {
    if (teamType.includeFullTeamType) {
      allUsers = [
        ...allUsers,
        ...[
          ...teamType.teamTypeManagers,
          /* eslint-disable-next-line no-unsafe-optional-chaining */
          ...teamType?.teams?.map((team) => [...team.teamDirectors, ...team.teamLieutenants, ...team.teamMembers]).flat(),
        ].map(mapAssignedFrom(teamType.teamTypeName)),
      ];
    } else {
      if (teamType.includeManager) {
        allUsers = [...allUsers, ...teamType.teamTypeManagers.map(mapAssignedFrom(`${teamType.teamTypeName} Manager`))];
      }

      const getAssignedFrom = (teamType: ITeamTypeSelection, team: ITeamSelection, memberType: TTeamMemberType) => {
        return team.includeFullTeam
          ? team.teamName
          : [teamIncludes(team, memberType) && team.teamName, teamTypeIncludes(teamType, memberType) && teamType.teamTypeName]
              .filter((i) => !!i)
              .map((i) => `${i} ${memberType}`)
              .join(', \n');
      };

      teamType.teams.forEach((team) => {
        if (teamTypeIncludes(teamType, TEAM_MEMBER_TYPES.Director) || teamIncludes(team, TEAM_MEMBER_TYPES.Director)) {
          allUsers = [
            ...allUsers,
            ...team.teamDirectors.map(mapAssignedFrom(getAssignedFrom(teamType, team, TEAM_MEMBER_TYPES.Director))),
          ];
        }

        if (teamTypeIncludes(teamType, TEAM_MEMBER_TYPES.Lieutenant) || teamIncludes(team, TEAM_MEMBER_TYPES.Lieutenant)) {
          allUsers = [
            ...allUsers,
            ...team.teamLieutenants.map(mapAssignedFrom(getAssignedFrom(teamType, team, TEAM_MEMBER_TYPES.Lieutenant))),
          ];
        }

        if (teamTypeIncludes(teamType, TEAM_MEMBER_TYPES.Member) || teamIncludes(team, TEAM_MEMBER_TYPES.Member)) {
          allUsers = [
            ...allUsers,
            ...team.teamMembers.map(mapAssignedFrom(getAssignedFrom(teamType, team, TEAM_MEMBER_TYPES.Member))),
          ];
        }
      });
    }

    if (groupOfUsers.includeAllManagers) {
      allUsers = [...allUsers, ...groupOfUsers.allManagers.map(mapAssignedFrom('Manager'))];
    }

    if (groupOfUsers.includeAllDirectors) {
      allUsers = [...allUsers, ...groupOfUsers.allTeamDirectors.map(mapAssignedFrom('Director'))];
    }

    if (groupOfUsers.includeAllLieutenants) {
      allUsers = [...allUsers, ...groupOfUsers.allTeamLieutenants.map(mapAssignedFrom('Lieutenant'))];
    }
  });
  return filterDuplicate(allUsers);
};

export const toggleTeamType = (teamType: ITeamTypeSelection, value: boolean) => ({
  ...teamType,
  includeFullTeamType: value,
  includeManager: value,
  includedTeamMemberType: value ? allMemberTypes : [],
  teams: teamType.teams.map((team) => ({
    ...team,
    includeFullTeam: value,
    includedTeamMemberType: value ? allMemberTypes : [],
  })),
});

export const applyCheckboxes = (
  data?: IGroupOfUsers,
  checkboxes?: IRoleAssignmentSelection
): IRoleAssignmentSelection | undefined => {
  const dataTeamTypeMap: IMap<ITeamType> = {};
  const checkboxTeamTypeMap: IMap<ITeamTypeSelection> = {};
  const checkboxTeamMap: IMap<ITeamSelection> = {};

  data?.teamType.forEach((teamType) => {
    dataTeamTypeMap[teamType.teamTypeId] = teamType;
  });

  checkboxes?.teamType.forEach((teamType) => {
    checkboxTeamTypeMap[teamType.teamTypeId] = teamType;
  });

  checkboxes?.teams.forEach((team) => {
    checkboxTeamMap[team.teamId] = team;
  });

  return (
    data &&
    checkboxes && {
      ...data,
      ...checkboxes,
      teamType: data.teamType.map((teamType) => {
        const checkboxTeamType = checkboxTeamTypeMap[teamType.teamTypeId];
        const dataTeamType = dataTeamTypeMap[teamType.teamTypeId];
        const checkboxTeamTypeTeamsMap: IMap<ITeamSelection> = {};

        checkboxTeamType?.teams?.forEach((team) => {
          checkboxTeamTypeTeamsMap[team.teamId] = team;
        });

        return {
          ...teamType,
          ...checkboxTeamType,
          teams: dataTeamType?.teams?.map((team) => ({
            ...team,
            ...checkboxTeamTypeTeamsMap[team.teamId],
            ...checkboxTeamMap[team.teamId],
          })),
        };
      }),
    }
  );
};

export const handleAllUsersCheck = (
  selection: IRoleAssignmentSelection,
  onChange: (selection: IRoleAssignmentSelection) => void
) => {
  selection.allUsers =
    selection.teamType.every(({ includeFullTeamType }) => includeFullTeamType) &&
    selection.includeAllManagers &&
    selection.includeAllDirectors &&
    selection.includeAllLieutenants;

  onChange(selection);
};
